import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    output,
    inject,
    HostBinding,
    signal,
    ViewChild,
    effect,
    computed,
    DestroyRef,
    input,
    Signal,
    ElementRef,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-app/shared/control-value-accessor-validator-base';
import {
    IOnboardingStep,
    IOnboardingStepElement,
    IOnboardingStepNotification,
} from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.d';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, NG_VALUE_ACCESSOR } from '@angular/forms';
import { fiFormatTwoDigit } from '@fi-shared/utils/fi-format-two-digits';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { IAvailableOnboardingStepElement } from '../../fi-onboarding';
import { FiOnboardingElementDropListHelperService } from '../fi-onboarding-element-drop-list-helper.service';
import { Animations } from '@fi-shared/utils/animations';
import { isDefined } from '@fi-shared/utils/is-defined';
import { getIconClassByObjectType } from '@fi-shared/utils/get-icon-class-by-object-type';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiOnboardingBuilderHelperService } from '../../fi-onboarding-builder-helper.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingBuilderStoreService } from '../../fi-onboarding-builder-store.service';
import { FiMatIconCheckboxMenuComponent } from '@fi-shared/fi-mat-icon-checkbox-menu/fi-mat-icon-checkbox-menu.component';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectTypes } from '@fi-app/shared/types';
import { NgModel } from '@angular/forms';

@Component({
    selector: 'fi-onboarding-section-step-editor',
    templateUrl: './fi-onboarding-section-step-editor.component.html',
    styleUrls: ['./fi-onboarding-section-step-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-onboarding-section-step-editor', '[class.highlighted]': 'isHighlighted()' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiOnboardingSectionStepEditorComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiOnboardingSectionStepEditorComponent,
            multi: true,
        },
    ],
    standalone: false,
    animations: [Animations.fadeHeight(), Animations.fadeWidth()],
})
export class FiOnboardingSectionStepEditorComponent extends ControlValueAccessorValidatorBase<IOnboardingStep> {
    public stepIndex = input.required<number>();
    public sectionIndex = input.required<number>();
    public isSectionDisabled = input<boolean>();
    public isViewMode = input<boolean>();

    public deleteStep = output<void>();
    public disableStepChange = output<boolean>();
    public openSendAutomatedNotificationDialog = output<void>();

    protected stepNameModelName = computed<string>(() => `step-name-${this.sectionIndex()}-${this.stepIndex()}`);

    private elementRef = inject(ElementRef);

    @HostBinding('class.step-disabled') get isStepEditorDisabled(): boolean {
        return this.isSectionDisabled() || this.ngModel.IsDisabled;
    }
    @HostBinding('class.invalid-highlight') get isComponentInvalidHighlighted(): boolean {
        const controlErrors = this.controlComponentErrors();
        return this.isErrorShown() && !isNullOrUndefined(controlErrors) && !!Object.keys(controlErrors).length;
    }

    protected getIconClassByObjectType = getIconClassByObjectType;
    protected isDefined = isDefined;
    protected fiFormatTwoDigit = fiFormatTwoDigit;

    protected fiOnboardingElementDropListHelperService = inject(FiOnboardingElementDropListHelperService);
    private fiOnboardingBuilderHelperService = inject(FiOnboardingBuilderHelperService);
    private appTextService = inject(AppTextService);
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private destroyRef = inject(DestroyRef);

    @ViewChild(FiMatIconCheckboxMenuComponent)
    private matIconCheckboxMenuComponent: FiMatIconCheckboxMenuComponent<IOnboardingStep>;

    private _actions: DeepReadonly<IItemsListAction2<IOnboardingStep>[]>;

    private isStepNameMaxLengthInvalid = signal(false);
    private stepNameValidationEffect = effect((onCleanup) => {
        const invalidModels = this.fiOnboardingBuilderHelperService.currentOnboardingInvalidModels();
        const stepNameModel = invalidModels.find((model: NgModel) => model.name === this.stepNameModelName());
        const updateMaxLengthStatus = (): void => {
            this.isStepNameMaxLengthInvalid.set(!!stepNameModel?.errors?.['maxlength']);
        };

        updateMaxLengthStatus();
        const statusChangesSubscription = stepNameModel?.statusChanges?.subscribe(updateMaxLengthStatus);
        onCleanup(() => statusChangesSubscription?.unsubscribe());
    });

    protected isHighlighted = signal<boolean>(false);
    private highlightedStepElementIdEffect = effect(() => {
        const highlightedStepElementId = this.fiOnboardingBuilderStoreService.highlightedStepElementId();
        const isHighlighted = this.elementRef.nativeElement.id === highlightedStepElementId;
        this.isHighlighted.set(isHighlighted);
        if (isHighlighted) {
            setTimeout(() => {
                this.isHighlighted.set(false);
            }, 5000);
        }
    });

    protected get actions(): DeepReadonly<IItemsListAction2<IOnboardingStep>[]> {
        if (!this._actions) {
            this._actions = this.createActions();
        }
        return this._actions;
    }
    protected selectedLanguageId: Signal<FiApplicationLanguageType> =
        this.fiOnboardingBuilderStoreService.selectedLanguageId;

    protected isErrorShown = signal(false);
    protected controlComponentErrors = signal<ValidationErrors | null>(null);
    protected isInputInvalid = computed<boolean>(() => {
        return (
            this.isErrorShown() &&
            (this.isStepNameMaxLengthInvalid() || !!this.controlComponentErrors()?.['missing-name'])
        );
    });
    protected isErrorBannerShown = computed<boolean>(() => {
        return this.isErrorShown() && !!this.controlComponentErrors()?.['missing-element'];
    });
    protected inputPlaceholder = computed<string>(() => {
        const placeholder = this.appTextService.get('onboarding.step_title');
        return this.isInputInvalid() ? `${placeholder}*` : placeholder;
    });

    private _isControlStatusSubscribed = false;
    private _formSubmitEffect = effect(() => {
        const formSubmittedCount = this.fiOnboardingBuilderStoreService.formSubmitted();
        if (!formSubmittedCount) {
            return;
        }
        this.isErrorShown.set(true);
        this.syncControlComponentErrors();
        this.subscribeToControlChanges();
    });

    protected isStepNameInputHintVisible = signal(false);
    protected stepNameInputClassName = computed<string>(() => {
        const classNames = ['step-name-input no-padding-form-field full-width'];
        if (!this.isStepNameInputHintVisible()) {
            classNames.push('no-subscript');
        }
        if (this.isInputInvalid()) {
            classNames.push('invalid-input');
        }
        return classNames.join(' ');
    });

    private syncControlComponentErrors(): void {
        this.controlComponentErrors.set(this.controlComponent?.errors ?? null);
    }

    private subscribeToControlChanges(): void {
        if (this._isControlStatusSubscribed || !this.controlComponent) {
            return;
        }
        const onControlChanges = (): void => {
            this.syncControlComponentErrors();
            this.cdr.markForCheck();
        };
        this.controlComponent.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(onControlChanges);
        this.controlComponent.valueChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(onControlChanges);
        this._isControlStatusSubscribed = true;
    }

    protected onStepItemDrop(
        $event: CdkDragDrop<
            IOnboardingStepElement | null, // target drop area data
            IAvailableOnboardingStepElement[], // source list data
            IAvailableOnboardingStepElement // dragged item data
        >,
    ): void {
        const availableStepElement: IAvailableOnboardingStepElement = $event.item.data;
        // https://chainformation.atlassian.net/browse/FI-6919
        // don't copy Id to Element
        this.ngModel.Element = {
            ObjectId: availableStepElement.ObjectId,
            ObjectType: availableStepElement.ObjectType,
            Name: availableStepElement.Name,
        };
        this.doPropagateChanges();
    }

    private handleSendAutomatedNotificationClick(): void {
        this.fiOnboardingBuilderHelperService
            .openAutomatedNotificationDialog(this.ngModel.Notifications)
            .onYesButtonClick((notifications: IOnboardingStepNotification[]) => {
                this.ngModel.Notifications = [...notifications];
                this._actions = this.createActions();
                this.doPropagateChanges();
                this.matIconCheckboxMenuComponent?.detectChanges();
                this.cdr.markForCheck();
            });
    }

    private handleDeleteStepClick(): void {
        if (this.ngModel.HasResponses) {
            this.fiOnboardingBuilderHelperService
                .openConfirmDeleteActiveItemDialog(
                    this.appTextService.get('onboarding.delete_step'),
                    this.appTextService.get('onboarding.confirm_delete_active_step_message'),
                )
                .onYesButtonClick((result) => {
                    if (result.isItemDeleted) {
                        this.deleteStep.emit();
                    } else if (result.isItemDisabled) {
                        this.disableStepChange.emit(true);
                    }
                });
            return;
        }
        if (!!this.ngModel.Element) {
            this.fiOnboardingBuilderHelperService
                .openConfirmDeleteItemNotActiveDialog(
                    this.appTextService.get('onboarding.delete_step'),
                    this.appTextService.get('onboarding.confirm_delete_not_active_step_message'),
                    this.appTextService.get('common.confirm'),
                )
                .onYesButtonClick(() => this.deleteStep.emit());
            return;
        }
        this.deleteStep.emit();
    }

    protected removeElement(): void {
        this.ngModel.Element = undefined;
        this.doPropagateChanges();
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        if (!this.isStepEditorDisabled) {
            return {
                'missing-element': !this.ngModel.Element,
                'missing-name': !this.ngModel.Names.some((name: IContentLanguageLabelModel) => name.Value),
            };
        }
    }

    private createActions(): DeepReadonly<IItemsListAction2<IOnboardingStep>[]> {
        return [
            {
                title: this.appTextService.get('automated_notification.send'),
                onClick: () => this.handleSendAutomatedNotificationClick(),
                iconClass: 'far fa-bell-ring',
                optionType: FiItemsListOptionType.Icon,
                isChecked: () => !!this.ngModel.Notifications.length,
            },
            {
                title: this.appTextService.get('onboarding.delete_step'),
                onClick: () => this.handleDeleteStepClick(),
                iconClass: 'far fa-trash',
                optionType: FiItemsListOptionType.Icon,
            },
            {
                title: this.appTextService.get('onboarding.disable_step'),
                onClick: () => this.disableStepChange.emit(!this.ngModel.IsDisabled),
                iconClass: 'fas fa-eye-slash',
                optionType: FiItemsListOptionType.Icon,
                isChecked: () => this.ngModel.IsDisabled,
            },
        ];
    }

    protected readonly ObjectTypes = ObjectTypes;
}
